import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { FileText, CalendarCheck, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayButtons = ({ kind, amountRef }) => (
  <PayPalButtons
    style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
    forceReRender={[kind]}
    createOrder={async () => {
      try {
        const payload = { kind };
        if (kind === "invoice") payload.amount = parseFloat(amountRef.current || "0");
        const { data } = await axios.post(`${API}/payments/paypal/order`, payload);
        return data.id;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't start the payment — check the amount.");
        throw e;
      }
    }}
    onApprove={async (data) => {
      try {
        const res = await axios.post(`${API}/payments/paypal/order/${data.orderID}/capture`);
        toast.success(`Payment of $${res.data.amount} received — thank you!`);
      } catch {
        toast.error("Payment couldn't be confirmed — contact us if you were charged.");
      }
    }}
    onError={() => toast.error("PayPal ran into a problem. Please try again.")}
  />
);

const PaySection = () => {
  const [cfg, setCfg] = useState(null);
  const [amount, setAmount] = useState("");
  const amountRef = useRef(amount);
  amountRef.current = amount;

  useEffect(() => {
    axios.get(`${API}/payments/config`).then(({ data }) => setCfg(data)).catch(() => setCfg({ configured: false }));
  }, []);

  return (
    <section id="pay" data-testid="payments-section-container" className="bg-[#0E0E12] py-24 lg:py-32">
      <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="eyebrow-label mb-4">06 · Pay Online</p>
          <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl lg:text-4xl">
            Settle up in seconds
          </h2>
          <p className="mt-4 max-w-xl text-base text-zinc-400">
            Pay your invoice or lock in a shoot date with a deposit — securely through PayPal.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55 }}
            data-testid="pay-invoice-card"
            className="luxury-card flex flex-col p-8"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <FileText size={20} className="text-[#E2C275]" />
            </span>
            <h3 className="font-display mt-6 text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">
              Pay an Invoice
            </h3>
            <p className="mt-2 text-xs text-zinc-400">
              Received a quote from us? Enter the agreed amount and pay securely.
            </p>
            <div className="mt-7">
              <label className="mb-2 block text-xs font-medium text-zinc-400">Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-zinc-500">$</span>
                <input
                  data-testid="pay-invoice-amount-input"
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="input-lux pl-8"
                />
              </div>
            </div>
            <div data-testid="pay-invoice-buttons" className="mt-6 flex-1">
              {cfg?.configured && <PayButtons kind="invoice" amountRef={amountRef} />}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: 0.12 }}
            data-testid="pay-deposit-card"
            className="luxury-card flex flex-col p-8"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <CalendarCheck size={20} className="text-[#E2C275]" />
            </span>
            <h3 className="font-display mt-6 text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">
              Booking Deposit
            </h3>
            <p className="mt-2 text-xs text-zinc-400">
              Lock in your shoot date with a ${cfg?.depositAmount || "100"} deposit — credited toward your final invoice.
            </p>
            <div className="mt-7 flex items-baseline gap-2">
              <span className="font-display text-3xl font-extrabold text-gold-gradient">
                ${cfg?.depositAmount || "100"}
              </span>
              <span className="text-xs text-zinc-500">refundable up to 48h before the shoot</span>
            </div>
            <div data-testid="pay-deposit-buttons" className="mt-6 flex-1">
              {cfg?.configured && <PayButtons kind="deposit" amountRef={amountRef} />}
            </div>
          </motion.div>
        </div>

        {cfg && !cfg.configured && (
          <p
            data-testid="pay-not-configured-note"
            className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-zinc-500"
          >
            <ShieldCheck size={14} className="text-[#D4AF37]" />
            Online payments are being connected — in the meantime, contact us and we'll send a PayPal invoice directly.
          </p>
        )}
        {cfg?.configured && (
          <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-zinc-500">
            <ShieldCheck size={14} className="text-[#D4AF37]" />
            Payments are processed securely by PayPal — we never see your card details.
          </p>
        )}
      </div>
    </section>
  );
};

const PaySectionWithProvider = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    axios.get(`${API}/payments/config`).then(({ data }) => setCfg(data)).catch(() => { });
  }, []);

  if (!cfg?.configured) return <PaySection />;
  return (
    <PayPalScriptProvider options={{ "client-id": cfg.clientId, currency: cfg.currency }}>
      <PaySection />
    </PayPalScriptProvider>
  );
};

export default PaySectionWithProvider;
